import { TranslationOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import { Button, Form, Input, InputNumber, Modal, Select, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  SalonService,
  SalonServiceUpdatePayload,
  SalonStylistOption,
} from "../../@type/salonService.types";
import { SalonAPI } from "../../api/Salon";

export interface ServiceCategoryOption {
  id: number;
  name: string;
}

interface ServiceEditModalProps {
  open: boolean;
  outletId: string;
  service: SalonService | null;
  categoryOptions: ServiceCategoryOption[];
  onCancel: () => void;
  onSuccess?: (updatedService: SalonService, nameChanged: boolean) => void;
  onManageTranslations?: () => void;
}

const formatNumber = (value: string | number | undefined) => {
  if (value === undefined || value === null || value === "") return "";
  const [integerPart, ...decimalParts] = `${value}`.split(".");
  const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalParts.length ? `${grouped}.${decimalParts.join("")}` : grouped;
};

// antd InputNumber types `parser` as returning a number, but a numeric string is what it expects here.
const parseNumber = (value: string | undefined) =>
  (value || "").replace(/[^\d.]/g, "") as unknown as number;

const blockNonNumericKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.ctrlKey || event.metaKey || event.key.length > 1) return;
  if (!/[\d.]/.test(event.key)) event.preventDefault();
};

interface ServiceEditFormValues {
  name: string;
  category_id: number | null;
  price: number | null;
  discount_price: number | null;
  duration: number | null;
  stylist_ids: number[];
}

const getErrorMessage = (error: unknown, fallback: string) => {
  const response = (error as { response?: { status?: number; data?: { message?: string } } })
    ?.response;

  if (response?.status === 403) {
    return "You do not have permission to edit this service";
  }

  if (response?.data?.message) return response.data.message;
  if (error instanceof Error) return error.message;
  return fallback;
};

const ServiceEditModal: React.FC<ServiceEditModalProps> = ({
  open,
  outletId,
  service,
  categoryOptions,
  onCancel,
  onSuccess,
  onManageTranslations,
}) => {
  const [form] = Form.useForm<ServiceEditFormValues>();
  const [saving, setSaving] = useState(false);
  const [stylists, setStylists] = useState<SalonStylistOption[]>([]);

  const allCategoryOptions = useMemo(() => {
    const options = [...categoryOptions];
    if (
      service?.category_id &&
      !options.some((option) => option.id === service.category_id)
    ) {
      options.push({
        id: service.category_id,
        name: service.category_name || `Category #${service.category_id}`,
      });
    }

    return options;
  }, [categoryOptions, service]);

  useEffect(() => {
    if (!open || !outletId) return;

    SalonAPI.getSalonStylists(outletId)
      .then((response) => setStylists(response.data.data || []))
      .catch((error) => {
        console.error("Error fetching salon stylists:", error);
        toast.error("Failed to load stylists");
      });
  }, [open, outletId]);

  useEffect(() => {
    if (!open || !service) return;

    form.setFieldsValue({
      name: service.name || "",
      category_id: service.category_id,
      price: service.price,
      discount_price: service.discount_price,
      duration: service.duration,
      stylist_ids: service.stylist_ids || [],
    });
  }, [form, open, service]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSave = async () => {
    if (!outletId || !service) return;

    try {
      const values = await form.validateFields();
      const payload: SalonServiceUpdatePayload = {
        name: values.name.trim(),
        price: values.price ?? null,
        discount_price: values.discount_price ?? null,
        duration: values.duration ?? null,
        category_id: values.category_id ?? null,
        stylist_ids: values.stylist_ids || [],
      };

      setSaving(true);
      const response = await SalonAPI.updateSalonService(outletId, service.id, payload);
      const nameChanged = payload.name !== (service.name || "").trim();
      toast.success("Service updated successfully");
      handleCancel();
      onSuccess?.(response.data.data, nameChanged);
    } catch (error) {
      const isValidationError =
        typeof error === "object" && error !== null && "errorFields" in error;

      if (!isValidationError) {
        console.error("Error updating salon service:", error);
        toast.error(getErrorMessage(error, "Failed to update service"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      destroyOnClose
      width={560}
      title={null}
      closable={false}
      styles={{ content: { borderRadius: "12px", overflow: "hidden", padding: 0 } }}
      footer={
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          px={3}
          py={2}
          borderTop="1px solid var(--app-border-light)"
        >
          <Button style={{ width: "120px" }} onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="primary"
            style={{ width: "120px", background: "#ba2156", border: "none" }}
            onClick={handleSave}
            loading={saving}
            disabled={!service || saving}
          >
            Save
          </Button>
        </Box>
      }
    >
      <Box bgcolor="#ba2156" color="#fff" px={3} py={2}>
        <Box fontSize="18px" fontWeight={700} lineHeight={1.3}>
          Edit Service
        </Box>
        <Box fontSize="12px" sx={{ opacity: 0.85 }}>
          {service?.name || "Service"}
        </Box>
      </Box>
      <Box px={3} py={3}>
      <Form form={form} layout="vertical" className="service-edit-form">
        <div
          style={{
            marginBottom: 16,
            padding: "16px",
            background: "var(--app-bg)",
            borderRadius: "6px",
            border: "1px solid var(--app-border-light)",
          }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: "Please enter service name" },
              {
                validator: (_, value) =>
                  String(value || "").trim()
                    ? Promise.resolve()
                    : Promise.reject(new Error("Please enter service name")),
              },
            ]}
          >
            <Input
              placeholder="Enter service name"
              suffix={
                <Tooltip title="Manage translations">
                  <Button
                    type="text"
                    size="small"
                    aria-label="Manage translations"
                    icon={<TranslationOutlined style={{ color: "#ba2156" }} />}
                    disabled={!service}
                    onClick={() => onManageTranslations?.()}
                  />
                </Tooltip>
              }
            />
          </Form.Item>

          <Form.Item
            label="Category"
            name="category_id"
            rules={
              allCategoryOptions.length > 0
                ? [{ required: true, message: "Please select category" }]
                : []
            }
          >
            <Select
              placeholder="Select category"
              disabled={allCategoryOptions.length === 0}
              options={allCategoryOptions.map((option) => ({
                value: option.id,
                label: option.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Price"
            name="price"
            rules={[{ required: true, message: "Please enter price" }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: "100%" }}
              formatter={formatNumber}
              parser={parseNumber}
              onKeyPress={blockNonNumericKey}
              placeholder="Enter price"
            />
          </Form.Item>

          <Form.Item
            label="Discount price"
            name="discount_price"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const price = getFieldValue("price");
                  if (value === null || value === undefined || value === "") {
                    return Promise.resolve();
                  }
                  if (price === null || price === undefined || price === "") {
                    return Promise.resolve();
                  }
                  return Number(value) <= Number(price)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Discount price must be less than or equal to price")
                      );
                },
              }),
            ]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: "100%" }}
              formatter={formatNumber}
              parser={parseNumber}
              onKeyPress={blockNonNumericKey}
              placeholder="Enter discount price"
            />
          </Form.Item>

          <Form.Item
            label="Duration (minutes)"
            name="duration"
            rules={[{ required: true, message: "Please enter duration" }]}
          >
            <InputNumber
              min={0}
              precision={0}
              style={{ width: "100%" }}
              formatter={formatNumber}
              parser={parseNumber}
              onKeyPress={blockNonNumericKey}
              placeholder="Enter duration"
            />
          </Form.Item>

          <Form.Item label="Stylists" name="stylist_ids">
            <Select
              mode="multiple"
              allowClear
              placeholder="Assign stylists"
              optionFilterProp="label"
              disabled={stylists.length === 0}
              options={stylists.map((stylist) => ({
                value: stylist.id,
                label: stylist.name,
              }))}
            />
          </Form.Item>
        </div>
      </Form>
      </Box>
    </Modal>
  );
};

export default ServiceEditModal;
